/**
 * <input-select> Web Component
 *
 * Custom select dropdown with animated caret and accessible keyboard navigation.
 */
class InputSelect extends HTMLElement {
  constructor() {
    super();
    this.select = null;
    this.trigger = null;
    this.dropdown = null;
    this.valueDisplay = null;
    this.isOpen = false;

    // Bind methods for listeners
    this.updatePositionBound = this.updatePosition.bind(this);
    this.scrollObserver = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.select = this.querySelector('select');
    this.trigger = this.querySelector('.input-select__trigger');
    // The dropdown gets teleported to <body> on first connect, so on a
    // reconnect (e.g. a third-party app re-rendering an ancestor) it's no
    // longer a descendant here — keep the cached reference instead of
    // clobbering it with a failed re-query.
    this.dropdown = this.dropdown || this.querySelector('.input-select__dropdown');
    this.valueDisplay = this.querySelector('.input-select__value');

    if (!this.select || !this.trigger || !this.dropdown) return;

    // Use MutationObserver in case options_html is dynamically loaded
    if (this.observer) this.observer.disconnect();
    this.observer = new MutationObserver(() => this.buildOptions());
    this.observer.observe(this.select, { childList: true });

    this.buildOptions();

    // Move dropdown to body by default; it will be re-parented to an open dialog when needed.
    if (this.dropdown.parentNode !== document.body) {
      document.body.appendChild(this.dropdown);
    }

    if (!this.boundTriggerClick) {
      this.boundTriggerClick = (e) => {
        if (this.isDisabled()) {
          return;
        }

        e.preventDefault();
        this.toggle();
      };
    }
    this.trigger.removeEventListener('click', this.boundTriggerClick);
    this.trigger.addEventListener('click', this.boundTriggerClick);

    // Close on outside click
    if (!this.boundOutsideClick) {
      this.boundOutsideClick = (e) => {
        if (!this.contains(e.target) && !this.dropdown.contains(e.target)) {
          this.close();
        }
      };
    }
    document.removeEventListener('click', this.boundOutsideClick);
    document.addEventListener('click', this.boundOutsideClick);

    // Update value if select changes externally
    if (!this.boundSelectChange) {
      this.boundSelectChange = this.updateDisplay.bind(this);
    }
    this.select.removeEventListener('change', this.boundSelectChange);
    this.select.addEventListener('change', this.boundSelectChange);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.boundOutsideClick) {
      document.removeEventListener('click', this.boundOutsideClick);
    }
    // Don't remove the dropdown from the document: it's parented to <body>,
    // not to this element, so it isn't actually part of the subtree being
    // disconnected, and a reconnect needs to find it still there.
    window.removeEventListener('scroll', this.scrollObserver, true);
    window.removeEventListener('resize', this.updatePositionBound);
  }

  updatePosition() {
    if (!this.isOpen) return;

    const teleportRoot = this.dropdown.parentElement || document.body;
    const rootRect =
      teleportRoot === document.body
        ? {
            top: 0,
            left: 0,
            bottom: window.innerHeight,
            width: window.innerWidth,
          }
        : teleportRoot.getBoundingClientRect();

    const rect = this.trigger.getBoundingClientRect();
    const dropdownHeight = this.dropdown.offsetHeight;
    const triggerTop = rect.top - rootRect.top;
    const triggerBottom = rect.bottom - rootRect.top;
    const spaceBelow = rootRect.bottom - rect.bottom;

    // Only set width if not borderless
    const isBorderless = this.dataset.borderless === 'true';
    if (!isBorderless) {
      this.dropdown.style.minWidth = `${rect.width}px`;
    }

    // Handle alignment
    const alignDropdown = this.dataset.alignDropdown || 'left';
    if (alignDropdown === 'right') {
      if (teleportRoot === document.body) {
        // Fixed positioning: right relative to viewport
        this.dropdown.style.right = `${window.innerWidth - rect.right - 15}px`;
      } else {
        // Absolute positioning: right relative to dialog
        this.dropdown.style.right = `${rootRect.width - (rect.right - rootRect.left)}px`;
      }
      this.dropdown.style.left = 'auto';
    } else {
      // Left alignment
      this.dropdown.style.left = `${rect.left - rootRect.left}px`;
      this.dropdown.style.right = 'auto';
    }

    if (spaceBelow < dropdownHeight && triggerTop > dropdownHeight) {
      // Show above if not enough space below and enough space above
      this.dropdown.style.top = `${triggerTop - dropdownHeight - 4}px`;
    } else {
      // Show below
      this.dropdown.style.top = `${triggerBottom + 4}px`;
    }
  }

  getTeleportRoot() {
    const dialog = this.closest('dialog');

    if (dialog && dialog.open) {
      return dialog;
    }

    return document.body;
  }

  ensureTeleportRoot() {
    const root = this.getTeleportRoot();

    if (this.dropdown.parentNode !== root) {
      root.appendChild(this.dropdown);
    }

    if (root === document.body) {
      this.dropdown.style.position = 'fixed';
      this.dropdown.style.zIndex = '9999';
      return;
    }

    this.dropdown.style.position = 'absolute';
    this.dropdown.style.zIndex = '30';
  }

  handleScroll(e) {
    if (this.isOpen && !this.dropdown.contains(e.target)) {
      this.updatePosition();
    }
  }

  buildOptions() {
    this.dropdown.innerHTML = '';
    const inputSize = this.dataset.inputSize || 'normal';
    let optionTypographyClass = 'body-sm';

    if (inputSize === 'small') {
      optionTypographyClass = 'body-xs';
    } else if (inputSize === 'large') {
      optionTypographyClass = 'body-base';
    }

    // Use querySelectorAll to find all options, bypassing any Shopify Theme Editor wrapper divs
    // that might prevent HTMLSelectElement.options from seeing them natively.
    const options = Array.from(this.querySelectorAll('option'));

    options.forEach((option) => {
      // Don't render the disabled placeholder in the custom dropdown list
      if (option.disabled && option.value === '') return;

      const li = document.createElement('li');
      li.className = `input-select__option ${optionTypographyClass} leading-6 truncate text-foreground p-2 rounded-sm cursor-pointer transition-colors duration-200`;
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', option.value);

      const iconHtml = option.dataset.iconHtml;
      if (iconHtml) {
        li.classList.add('flex', 'items-center', 'gap-2');

        const iconWrap = document.createElement('span');
        iconWrap.className = 'input-select__option-icon flex-none';
        iconWrap.innerHTML = iconHtml;

        const textWrap = document.createElement('span');
        textWrap.className = 'truncate';
        textWrap.textContent = option.textContent;

        li.appendChild(iconWrap);
        li.appendChild(textWrap);
      } else {
        li.textContent = option.textContent;
      }

      if (option.selected) {
        li.setAttribute('aria-selected', 'true');
      }

      li.addEventListener('click', () => {
        if (this.isDisabled()) {
          return;
        }

        this.select.value = option.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
      });

      this.dropdown.appendChild(li);
    });

    this.updateDisplay();
  }

  updateDisplay() {
    const selectedOption =
      this.querySelector('option:checked') || this.querySelector('option');
    if (selectedOption) {
      const iconHtml = selectedOption.dataset.iconHtml;
      if (iconHtml) {
        this.valueDisplay.innerHTML = `<span class="input-select__value-icon inline-flex flex-none items-center">${iconHtml}</span><span class="truncate">${selectedOption.textContent}</span>`;
        this.valueDisplay.classList.add('flex', 'items-center', 'gap-2');
      } else {
        this.valueDisplay.textContent = selectedOption.textContent;
      }
      if (selectedOption.disabled && selectedOption.value === '') {
        this.valueDisplay.style.opacity = '0.5';
      } else {
        this.valueDisplay.style.opacity = '1';
      }
    }

    // Update aria-selected state
    const customOptions = this.dropdown.querySelectorAll(
      '.input-select__option',
    );
    customOptions.forEach((opt) => {
      if (opt.getAttribute('data-value') === this.select.value) {
        opt.setAttribute('aria-selected', 'true');
      } else {
        opt.setAttribute('aria-selected', 'false');
      }
    });
  }

  setValue(value, options = {}) {
    if (!this.select) return;

    const { emitChange = false } = options;
    const nextValue = value == null ? '' : String(value);
    const hasOption = Array.from(this.select.options).some(
      (option) => option.value === nextValue,
    );

    if (!hasOption) return;

    const changed = this.select.value !== nextValue;
    this.select.value = nextValue;
    this.updateDisplay();

    if (emitChange && changed) {
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  toggle() {
    if (this.isDisabled()) {
      this.close();
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  isDisabled() {
    return Boolean(this.select && this.select.disabled);
  }

  open() {
    this.isOpen = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.ensureTeleportRoot();
    // Ensure display block to calculate offsetHeight before position calc
    this.dropdown.classList.remove('hidden');

    this.updatePosition();

    const caret = this.querySelector('.input-select__caret');
    if (caret) caret.classList.add('rotate-180');

    // Add scroll tracking with capture = true to grab inner scroll containers
    window.addEventListener('scroll', this.scrollObserver, true);
    window.addEventListener('resize', this.updatePositionBound);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.dropdown.classList.add('hidden');

    const caret = this.querySelector('.input-select__caret');
    if (caret) caret.classList.remove('rotate-180');

    window.removeEventListener('scroll', this.scrollObserver, true);
    window.removeEventListener('resize', this.updatePositionBound);
  }
}

const getInputSelectElements = (root = document) => {
  if (!(root instanceof Element)) {
    return Array.from(document.querySelectorAll('input-select'));
  }

  const elements = [];

  if (root.matches('input-select')) {
    elements.push(root);
  }

  elements.push(...root.querySelectorAll('input-select'));

  return elements;
};

window.refreshWakaInputSelects = (root = document) => {
  getInputSelectElements(root).forEach((element) => {
    if (typeof element.buildOptions === 'function') {
      element.buildOptions();
    }
  });
};

if (!customElements.get('input-select')) {
  customElements.define('input-select', InputSelect);
}
